import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AnimatePresence, motion } from "framer-motion";
import { EditText } from "react-edit-text";
import { addNewWordCrossword, removeWordCrossword, updateCrosswordDescription, updateCrosswordGrid, updateSelectedComponentType } from "../../../../../redux/slices/userActivity";
import { lightOrDark } from "../../../../../utils";

import styles from "./CrossWords.module.css";

const CrossWords = ({ master, style }) => {
  const dispatch = useDispatch();

  const { selectedUserActivity } = useSelector((state) => state.userActivity);
  const { modalOpen: previewModalOpen, showAnswers, isMobile } = useSelector((state) => state.preview);

  const { grid_size, word_set, description } = master.question_id;
  const [cellSize, setCellSize] = useState(30);

  const [isBackgroundLight, setIsBackgroundLight] = useState(false);

  useEffect(() => {
    dispatch(
      updateSelectedComponentType({
        type: "slide",
        name: "container",
      })
    );
  }, []);

  const [gridLayoutError, setGridLayoutError] = useState(false);
  useEffect(() => {
    if (gridLayoutError) {
    }
  }, [gridLayoutError]);

  const updateGrid = (grid_size) => {
    for (let i = 0; i < word_set.length; i++) {
      for (let j = 0; j < word_set[i].position.length; j++) {
        if (word_set[i].position[j].y >= grid_size || word_set[i].position[j].x >= grid_size) {
          setGridLayoutError(true);
          setTimeout(() => {
            setGridLayoutError(false);
          }, 1500);
          return;
        }
      }
    }
    setHighlightMode(false);
    setHighlightLayout([]);
    setTimeout(() => {
      inputRef.current.focus();
      inputRef.current.select();
      document.body.removeEventListener("keydown", removeHighlightMode);
    }, 100);

    dispatch(
      updateCrosswordGrid({
        selectedActivityIdx: selectedUserActivity,
        grid_size,
      })
    );
  };

  const inputRef = useRef(null);

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const getRandomCharacter = () => {
    return alphabet[Math.floor(Math.random() * alphabet.length)];
  };

  const [layout, setLayout] = useState([]);
  const changeLayout = () => {
    // Generate the Grid Layout
    let newLayout = [];
    for (let xIdx = 0; xIdx < grid_size; xIdx++) {
      for (let yIdx = 0; yIdx < grid_size; yIdx++) {
        newLayout.push({
          i: `x${xIdx}-y${yIdx}`,
          letter: previewModalOpen && !showAnswers ? getRandomCharacter() : "",
          x: xIdx,
          y: yIdx,
          xPos: xIdx * cellSize,
          yPos: yIdx * cellSize,
          w: cellSize,
          h: cellSize,
        });
      }
    }
    word_set.map((word) => {
      word.position.map((pos, i) => {
        newLayout = newLayout.map((item) => (item.x === pos.x && item.y === pos.y ? { ...item, letter: pos.letter } : item));
      });
    });
    setLayout(newLayout);
  };
  useEffect(() => changeLayout(), [previewModalOpen, showAnswers]);

  const [highlightMode, setHighlightMode] = useState(false);
  const [highlightLayout, setHighlightLayout] = useState([]);

  useEffect(() => {
    if (grid_size === 8) setCellSize(() => 35);
    if (grid_size === 10) setCellSize(() => 28);
    if (grid_size === 12) setCellSize(() => 23.3);
    changeLayout();
  }, [grid_size, cellSize, word_set]);

  useEffect(() => {
    if (!style.container?.backgroundColor) return;
    if (lightOrDark(style.container?.backgroundColor) === "light") {
      setIsBackgroundLight(true);
    } else if (lightOrDark(style.container?.backgroundColor) === "dark") {
      setIsBackgroundLight(false);
    }
  }, [style.container?.backgroundColor]);

  const [newWord, setNewWord] = useState("");
  const handleInputChange = (e) => {
    setNewWord(e.target.value.split(" ").join("").toUpperCase());
  };
  const handleNewWordSubmit = () => {
    if (newWord.length < 2 || highlightMode) return;
    setHighlightMode(true);
  };

  const removeHighlightMode = (e) => {
    if (e.key === "Escape") {
      setHighlightMode(false);
      setHighlightLayout([]);
      setTimeout(() => {
        inputRef.current.focus();
        inputRef.current.select();
        document.body.removeEventListener("keydown", removeHighlightMode);
      }, 100);
    }
  };

  useEffect(() => {
    if (!highlightMode) return;
    document.body.addEventListener("keydown", removeHighlightMode);
  }, [highlightMode]);

  const addWord = (x, y, direction, word) => {
    const letters = word.split("");
    let position = [];
    const id = Math.floor(Math.random() * 1000000 * letters.length);

    if (direction === "XP") {
      for (let i = 0; i < letters.length; i++) {
        position.push({ x: x + i, y: y, letter: letters[i] });
      }
    }
    if (direction === "YP") {
      for (let i = 0; i < letters.length; i++) {
        position.push({ x: x, y: y + i, letter: letters[i] });
      }
    }
    if (direction === "XN") {
      for (let i = 0; i < letters.length; i++) {
        position.push({ x: x - i, y: y, letter: letters[i] });
      }
    }
    if (direction === "YN") {
      for (let i = 0; i < letters.length; i++) {
        position.push({ x: x, y: y - i, letter: letters[i] });
      }
    }
    dispatch(
      addNewWordCrossword({
        selectedActivityIdx: selectedUserActivity,
        newWord: { id, word, position },
      })
    );
    setHighlightLayout([]);
    setNewWord("");
    setHighlightMode(false);
  };

  const removeWord = (id) => {
    dispatch(
      removeWordCrossword({
        selectedActivityIdx: selectedUserActivity,
        wordId: id,
      })
    );
    // setWords(word_set.filter((word) => word.id !== id));
  };

  // --> Check direction for empty space
  const checkXP = (x, y, length) => {
    if (x + length > grid_size) return false;
    for (let i = 0; i < word_set.length; i++) {
      for (let j = 0; j < word_set[i].position.length; j++) {
        if (y === word_set[i].position[j].y && x + length > word_set[i].position[j].x && word_set[i].position[j].x >= x) {
          return false;
        }
      }
    }
    return true;
  };
  const checkYP = (x, y, length) => {
    if (y + length > grid_size) return false;
    for (let i = 0; i < word_set.length; i++) {
      for (let j = 0; j < word_set[i].position.length; j++) {
        if (x === word_set[i].position[j].x && y + length > word_set[i].position[j].y && word_set[i].position[j].y >= y) {
          return false;
        }
      }
    }
    return true;
  };
  const checkXN = (x, y, length) => {
    if (x - length + 1 < 0) return false;
    for (let i = 0; i < word_set.length; i++) {
      for (let j = 0; j < word_set[i].position.length; j++) {
        if (y === word_set[i].position[j].y && x - length < word_set[i].position[j].x && word_set[i].position[j].x <= x) {
          return false;
        }
      }
    }
    return true;
  };
  const checkYN = (x, y, length) => {
    if (y - length + 1 < 0) return false;
    for (let i = 0; i < word_set.length; i++) {
      for (let j = 0; j < word_set[i].position.length; j++) {
        if (x === word_set[i].position[j].x && y - length < word_set[i].position[j].y && word_set[i].position[j].y <= y) {
          return false;
        }
      }
    }
    return true;
  };

  const showHighlight = (x, y, wordLength) => {
    if (!highlightMode) return;

    setHighlightLayout([]);

    const XP = checkXP(x, y, wordLength);
    const YP = checkYP(x, y, wordLength);
    const XN = checkXN(x, y, wordLength);
    const YN = checkYN(x, y, wordLength);
    setHighlightLayout((prev) => {
      return prev.concat({ i: `XP`, letter: ``, x: x, y: y, xPos: x * cellSize, yPos: y * cellSize, w: cellSize * wordLength, h: cellSize, direction: "XP", visibile: XP });
    });
    setHighlightLayout((prev) => {
      return prev.concat({ i: `YP`, letter: ``, x: x, y: y, xPos: x * cellSize, yPos: y * cellSize, w: cellSize, h: cellSize * wordLength, direction: "YP", visibile: YP });
    });
    setHighlightLayout((prev) => {
      return prev.concat({ i: `XN`, letter: ``, x: x, y: y, xPos: (x - wordLength + 1) * cellSize, yPos: y * cellSize, w: cellSize * wordLength, h: cellSize, direction: "XN", visibile: XN });
    });
    setHighlightLayout((prev) => {
      return prev.concat({ i: `YN`, letter: ``, x: x, y: y, xPos: x * cellSize, yPos: (y - wordLength + 1) * cellSize, w: cellSize, h: cellSize * wordLength, direction: "YN", visibile: YN });
    });
    setHighlightLayout((prev) => {
      return prev.concat({ i: `close`, letter: ``, x: x, y: y, xPos: x * cellSize, yPos: y * cellSize, w: cellSize, h: cellSize, visibile: XP || XN || YP || YN });
    });
  };

  const onDescriptionChange = (e) => {
    // console.log(e.target.value);
    dispatch(
      updateCrosswordDescription({
        selectedActivityIdx: selectedUserActivity,
        description: e.target.value,
      })
    );
  };
  return (
    <div
      className={styles.hidden_words_wrapper}
      style={{ pointerEvents: previewModalOpen ? "none" : "all" }}
      onClick={() =>
        dispatch(
          updateSelectedComponentType({
            type: "slide",
            name: "container",
          })
        )
      }
    >
      <div className={styles.select_grid_container}>
        <h3 style={{ color: isBackgroundLight ? "black" : "white" }}>{previewModalOpen ? "Selected Grid:" : " 1. Select Grid:"}</h3>
        <button
          className={`${styles.select_grid_btn} ${grid_size === 8 ? styles.selected : ""}`}
          style={{
            borderColor: grid_size === 8 && isBackgroundLight ? "black" : "#bebebe",
            display: previewModalOpen && grid_size !== 8 && "none",
          }}
          onClick={() => updateGrid(8)}
        >
          8 x 8
        </button>
        <button
          className={`${styles.select_grid_btn} ${grid_size === 10 ? styles.selected : ""}`}
          style={{
            borderColor: grid_size === 10 && isBackgroundLight ? "black" : "#bebebe",
            display: previewModalOpen && grid_size !== 10 && "none",
          }}
          onClick={() => updateGrid(10)}
        >
          10 x 10
        </button>
        <button
          className={`${styles.select_grid_btn} ${grid_size === 12 ? styles.selected : ""}`}
          style={{
            borderColor: grid_size === 12 && isBackgroundLight ? "black" : "#bebebe",
            display: previewModalOpen && grid_size !== 12 && "none",
          }}
          onClick={() => updateGrid(12)}
        >
          12 x 12
        </button>
      </div>

      <div className={`${styles.main_container} ${isMobile ? styles.mobileMainContainer : ''}`}>
        <div className={styles.main_container__left}>
          {/* Input Container  */}
          <div style={{ display: previewModalOpen ? "none" : "flex" }} className={styles.input_container}>
            <h3 style={{ color: isBackgroundLight ? "black" : "white" }}>2. Type a word:</h3>
            <div className={styles.word_input_div}>
              <div style={{ position: "relative" }}>
                <input
                  ref={inputRef}
                  placeholder=""
                  className={styles.word_input}
                  type="text"
                  value={newWord}
                  maxLength={grid_size}
                  disabled={highlightMode}
                  onChange={handleInputChange}
                  onKeyDown={(e) => e.key === "Enter" && handleNewWordSubmit()}
                  // onFocus={(e) => e.target.select()}
                />
                <svg
                  onClick={() => (
                    setHighlightMode(false),
                    setHighlightLayout([]),
                    setTimeout(() => {
                      inputRef.current.focus();
                      inputRef.current.select();
                    }, 100)
                  )}
                  className={styles.word_input__close}
                  style={{ visibility: highlightMode ? "visible" : "hidden" }}
                  width="18px"
                  height="18px"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zM11.4 10l2.83-2.83-1.41-1.41L10 8.59 7.17 5.76 5.76 7.17 8.59 10l-2.83 2.83 1.41 1.41L10 11.41l2.83 2.83 1.41-1.41L11.41 10z" />
                </svg>
              </div>
              <button className={styles.word_input__enter_btn} disabled={newWord.length < 2 ? true : false || highlightMode} onClick={handleNewWordSubmit}>
                <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M19 0C19.2449 3.23106e-05 19.4813 0.089956 19.6644 0.252715C19.8474 0.415474 19.9643 0.639749 19.993 0.883L20 1V7.5C20.0001 8.39507 19.6572 9.25615 19.0419 9.90621C18.4267 10.5563 17.5857 10.9459 16.692 10.995L16.5 11H3.415L6.707 14.293C6.87918 14.4652 6.98261 14.6943 6.99789 14.9373C7.01317 15.1803 6.93925 15.4206 6.79 15.613L6.707 15.707C6.53481 15.8792 6.30571 15.9826 6.06268 15.9979C5.81965 16.0132 5.5794 15.9393 5.387 15.79L5.293 15.707L0.293 10.707C0.257971 10.6721 0.225551 10.6347 0.196 10.595L0.125 10.485L0.0710001 10.371L0.036 10.266L0.0109999 10.149L0.00399995 10.089L0 10L0.00300002 9.925L0.02 9.799L0.05 9.688L0.0940001 9.577L0.146 9.479L0.21 9.387L0.293 9.293L5.293 4.293C5.47296 4.11365 5.71443 4.00953 5.96838 4.00177C6.22233 3.99402 6.46971 4.08321 6.66028 4.25125C6.85084 4.41928 6.97031 4.65355 6.9944 4.90647C7.01849 5.1594 6.94541 5.41201 6.79 5.613L6.707 5.707L3.415 9H16.5C16.8727 9.00001 17.2321 8.86124 17.5081 8.61075C17.7841 8.36026 17.957 8.01598 17.993 7.645L18 7.5V1C18 0.734784 18.1054 0.48043 18.2929 0.292893C18.4804 0.105357 18.7348 0 19 0Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Layout Container  */}

          <div className="" style={{ marginTop: "10px" }}>
            <h3 style={{ color: isBackgroundLight ? "black" : "white", display: previewModalOpen ? "none" : "block" }}>3. Select a location in the grid:</h3>

            <div
              className={gridLayoutError && styles.layout_error_animate}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(
                  updateSelectedComponentType({
                    type: "crossword_grid",
                    name: "grid",
                  })
                );
              }}
              style={{
                minWidth: "279px",
                minHeight: "279px",
                maxWidth: "280px",
                maxHeight: "280px",
                width: "fit-content",
                height: "fit-content",
                overflow: "hidden",
                marginTop: "8px",
                backgroundColor: "white",
                transition: "all 0.1s cubic-bezier(0.65, 0.05, 0.36, 1) 0s",
                outline: gridLayoutError && "2px solid red",
                boxShadow: highlightMode ? "rgb(145 145 145 / 50%) 0px 0px 4px 2px" : "#68686887 0px 0px 3px 0px",
                transform: highlightMode ? "scale(1.015)" : previewModalOpen ? "scale(1.1)" : "scale(1)",
                transformOrigin: "left top",
              }}
            >
              <div
                className={styles.layout_container}
                style={{
                  position: "relative",
                  height: `${cellSize * grid_size}px`,
                  width: `${cellSize * grid_size}px`,
                  opacity: highlightMode ? "1" : "0.9",
                  // boxShadow: highlightMode ? "rgb(19 2 50) 0px 0px 10px 2px inset" : "",
                }}
              >
                {layout.map((item) => {
                  return (
                    <div
                      className={`${styles.layout__cell}`}
                      style={{
                        backgroundColor: style.grid?.backgroundColor || "#fff",
                        transform: `translate(${item.xPos}px, ${item.yPos}px)`,
                        width: `${item.w}px`,
                        height: `${item.h}px`,
                        cursor: "copy",
                        pointerEvents: highlightMode ? "all" : "none",
                      }}
                      data-key={item.i}
                      onClick={() => showHighlight(item.x, item.y, newWord?.length)}
                      key={item.i}
                    >
                      <AnimatePresence>
                        {item.letter ? (
                          <motion.div
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.5, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{ color: style.grid?.color || "black", fontFamily: style.grid?.fontFamily || "sans" }}
                          >
                            {item.letter.toUpperCase()}
                          </motion.div>
                        ) : (
                          ""
                        )}
                      </AnimatePresence>
                      {/* <EditText value={item.letter} /> */}
                    </div>
                  );
                })}
                {highlightLayout.map((item) => {
                  return item.i === "close" ? (
                    <div
                      className={`${styles.layout__cell} ${styles.layout__cell_highlight}`}
                      style={{
                        backgroundColor: "red",
                        visibility: item.visibile ? "visible" : "hidden",
                        opacity: item.visibile ? "1" : "0",
                        transform: `translate(${item.xPos}px, ${item.yPos}px)`,
                        width: `${item.w}px`,
                        height: `${item.h}px`,
                        cursor: "pointer",
                      }}
                      data-key={item.i}
                      onClick={() => setHighlightLayout([])}
                      key={item.i}
                    >
                      <svg
                        width="18px"
                        height="18px"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                        // {...props}
                      >
                        <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zM11.4 10l2.83-2.83-1.41-1.41L10 8.59 7.17 5.76 5.76 7.17 8.59 10l-2.83 2.83 1.41 1.41L10 11.41l2.83 2.83 1.41-1.41L11.41 10z" />
                      </svg>
                    </div>
                  ) : (
                    <div
                      className={`${styles.layout__cell} ${styles.layout__cell_highlight}`}
                      style={{
                        transform: `translate(${item.xPos}px, ${item.yPos}px)`,
                        width: `${item.w}px`,
                        height: `${item.h}px`,
                        visibility: item.visibile ? "visible" : "hidden",
                        opacity: item.visibile ? "1" : "0",
                        // backgroundColor: "#b7e5da",
                        border: "1px solid #289379",
                        boxShadow: "inset #40ad93 0px 0px 7px 0px",
                      }}
                      data-key={item.i}
                      onClick={() => addWord(item.x, item.y, item.direction, newWord)}
                      key={item.i}
                    ></div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Side Container  */}
        <div style={{ marginTop: previewModalOpen && "20px" }} className={styles.main_container__right}>
          <div className={styles.words_added_box}>
            <h3 className={styles.words_added_box__heading} style={{ color: isBackgroundLight ? "black" : "white" }}>
              Words added:
            </h3>
            <div className={styles.words_added_box__list}>
              {word_set.map((word, idx) => (
                <div key={idx} className={styles.words_added_box__list_item}>
                  <p style={{ color: isBackgroundLight ? "black" : "white" }}>{word.word}</p>
                  <svg
                    style={{ visibility: previewModalOpen ? "hidden" : "visible" }}
                    onClick={() => removeWord(word.id)}
                    className={styles.words_added_box__remove_btn}
                    width="12"
                    height="13"
                    viewBox="0 0 10 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect y="0.5" width="10.0001" height="10.0001" rx="5.00006" fill="black" fillOpacity="0.5" />
                    <path
                      d="M7.84633 3.39629C8.05137 3.19125 8.05137 2.85882 7.84633 2.65378C7.64129 2.44874 7.30886 2.44874 7.10382 2.65378L5.00006 4.75754L2.89629 2.65378C2.69125 2.44874 2.35882 2.44874 2.15378 2.65378C1.94875 2.85882 1.94875 3.19125 2.15378 3.39629L4.25755 5.50005L2.15378 7.60382C1.94874 7.80886 1.94874 8.14129 2.15378 8.34633C2.35882 8.55137 2.69125 8.55137 2.89629 8.34633L5.00006 6.24256L7.10383 8.34633C7.30887 8.55137 7.6413 8.55137 7.84634 8.34633C8.05138 8.14129 8.05138 7.80886 7.84634 7.60383L5.74256 5.50005L7.84633 3.39629Z"
                      fill="white"
                    />
                  </svg>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.description_box}>
            <h3 className={styles.description_box__heading} style={{ color: isBackgroundLight ? "black" : "white" }}>
              {previewModalOpen ? "Short description:" : "Add short description: (optional)"}
            </h3>
            <textarea className={styles.description_box__input} style={{}} name="" id="" cols="20" onChange={onDescriptionChange} value={description} rows="4"></textarea>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossWords;
